import { useCallback, useState } from "react";
import type { ClipboardMode, RebasableAssignment } from "@/lib/assignmentClipboard";

export interface ClipboardEntry<T> {
  assignment: T;
  mode: ClipboardMode;
}

export interface UseAssignmentClipboard<T> {
  /** Current clipboard contents, or null when empty. */
  entry: ClipboardEntry<T> | null;
  /** True while something is on the clipboard (drives "Paste" enabled state). */
  hasClipboard: boolean;
  /** Place a copy on the clipboard (original is kept). */
  copy: (assignment: T) => void;
  /** Place a cut on the clipboard (original is removed once pasted). */
  cut: (assignment: T) => void;
  /** Empty the clipboard. */
  clear: () => void;
}

/**
 * Holds the single in-memory clipboard slot for the crew schedule
 * copy/cut/paste flow. Deliberately tiny and storage-free: the clipboard is
 * scoped to the editor session and never touches the OS clipboard, so a
 * producer copying an assignment can't accidentally clobber unrelated
 * copied text and vice-versa.
 *
 * Kept separate from CrewSchedule so the clipboard contract is testable on
 * its own and reusable should another schedule surface need it.
 */
export function useAssignmentClipboard<
  T extends RebasableAssignment = RebasableAssignment,
>(): UseAssignmentClipboard<T> {
  const [entry, setEntry] = useState<ClipboardEntry<T> | null>(null);

  const copy = useCallback((assignment: T) => {
    setEntry({ assignment, mode: "copy" });
  }, []);

  const cut = useCallback((assignment: T) => {
    setEntry({ assignment, mode: "cut" });
  }, []);

  const clear = useCallback(() => {
    setEntry(null);
  }, []);

  return { entry, hasClipboard: entry !== null, copy, cut, clear };
}
